import { useMemo } from 'react'
import StarIcons from '@/icons/StarIcons'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { ListStarredObjects } from '@/modules/Drive/services'
import useAuthStore from '@/stores/useAuthStore'
import useDriveStore from '@/stores/useDriveStore'
import { Folder, File as DriveFile, ObjectType } from '@/modules/Drive/types'
import FileGrid from '../FileGrid'
import ViewTypeSwitch from '../ViewTypeSwitch'
import { t } from 'i18next'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useSearchParams } from 'react-router-dom'

const StarredFile = () => {
    const employee = useAuthStore((state) => state.employee)
    const viewType = useDriveStore((state) => state.viewType)
    const setViewType = useDriveStore((state) => state.setViewType)
    const { data, isLoading, isError, error } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.DRIVE_STARRED_LIST],
        queryFn: () => ListStarredObjects(employee?.employee_uuid || ''),
        enabled: !!employee?.employee_uuid,
    })

    const [searchParams] = useSearchParams()
    const searchQuery = searchParams.get('search')?.toLowerCase() ?? ''

    const foldersAndFiles = useMemo(() => {
        const items = data?.results || []
        const folders: Folder[] = items
            .filter((o: any) => o.starred_object_type === ObjectType.FOLDER)
            .filter((o: any) =>
                o.starred_object_key
                    .replace(/\/$/, '')
                    .split('/')
                    .pop()
                    ?.toLowerCase()
                    ?.includes(searchQuery)
            )
            .map((o: any) => ({ Prefix: o.starred_object_key }))
        const files: DriveFile[] = items
            .filter((o: any) => o.starred_object_type === ObjectType.FILE)
            .filter((o: any) =>
                o.starred_object_key
                    .split('/')
                    .pop()
                    ?.toLowerCase()
                    ?.includes(searchQuery)
            )
            .map((o: any) => ({
                Key: o.starred_object_key,
                Size: o.starred_object_size,
                LastModified: o.starred_object_last_modified,
            }))
        return { folders, files }
    }, [data, searchQuery])

    if (isLoading) {
        return (
            <div className="flex flex-col gap-4">
                <header className="flex items-center justify-between">
                    <div className="font-semibold text-primary text-base flex items-center gap-3">
                        <StarIcons className="w-5 h-5" />
                        <span>{t('starred-files-title')}</span>
                    </div>
                </header>
                <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500">
                        {t('drive.loading-starred')}
                    </div>
                </div>
            </div>
        )
    }

    if (isError) {
        return (
            <div className="flex flex-col gap-4">
                <header className="flex items-center justify-between">
                    <div className="font-semibold text-primary text-base flex items-center gap-3">
                        <StarIcons className="w-5 h-5" />
                        <span>{t('starred-files-title')}</span>
                    </div>
                </header>
                <div className="flex items-center justify-center py-8">
                    <div className="text-red-500">
                        {t('drive.error-loading-starred')}:{' '}
                        {error?.message || t('something-went-wrong')}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-4">
            <header className="flex items-center justify-between">
                <div className="font-semibold text-primary text-base flex items-center gap-3">
                    <StarIcons className="w-5 h-5" />
                    <span>{t('starred-files-title')}</span>
                </div>
                <ViewTypeSwitch value={viewType} onChange={setViewType} />
            </header>

            {foldersAndFiles.folders.length === 0 &&
            foldersAndFiles.files.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500">
                        {t('empty-starred-title')}
                    </div>
                </div>
            ) : (
                <FileGrid
                    folders={foldersAndFiles.folders}
                    files={foldersAndFiles.files}
                />
            )}
        </div>
    )
}

export default StarredFile
